import { DropdownItem, IconButton, DropdownMenu } from '@dev-spendesk/grapes';
import { useMemo } from 'react';

import {
  type I18nKey,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import { track } from 'src/core/utils/analytics';
import { AnalyticEventName } from 'src/core/utils/analytics/eventNames';

type MenuOption = {
  key: 'edit' | 'delete' | 'updateBudgets';
  hasSeparator: boolean;
  action: () => void;
};

const dropdownOptionToI18NKey: Record<
  'edit' | 'delete' | 'updateBudgets',
  I18nKey
> = {
  edit: 'budget.budgetaryExercise.dropdown.edit',
  delete: 'budget.budgetaryExercise.dropdown.delete',
  updateBudgets: 'budget.budgetaryExercise.dropdown.updateBudgets',
};

export type BudgetaryExerciseDropdownMenuProps = {
  hasBudgets: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onUpdateBudgets: () => void;
};

export const BudgetaryExerciseDropdownMenu = ({
  hasBudgets,
  onEdit,
  onDelete,
  onUpdateBudgets,
}: BudgetaryExerciseDropdownMenuProps) => {
  const { t } = useTranslation('global');

  const options = useMemo(() => {
    const opts: MenuOption[] = [
      {
        key: 'edit',
        hasSeparator: false,
        action: onEdit,
      },
      {
        key: 'delete',
        hasSeparator: false,
        action: () => {
          track(
            AnalyticEventName.BUDGETS_BUDGETARY_EXERCISE_DELETE_BUTTON_CLICKED,
          );
          onDelete();
        },
      },
    ];

    if (hasBudgets) {
      opts.unshift({
        key: 'updateBudgets',
        hasSeparator: true,
        action: () => {
          track(
            AnalyticEventName.BUDGETS_BUDGETARY_EXERCISE_EDIT_BUTTON_CLICKED,
          );
          onUpdateBudgets();
        },
      });
    }

    return opts;
  }, [hasBudgets, onEdit, onDelete, onUpdateBudgets]);

  return (
    <DropdownMenu
      options={options.map((option) => ({
        label: t(dropdownOptionToI18NKey[option.key]),
        ...option,
      }))}
      placement="bottom-end"
      renderButton={(getToggleButtonProps) => {
        return (
          <IconButton
            {...getToggleButtonProps()}
            onClick={(event) => {
              track(
                AnalyticEventName.BUDGETS_BUDGETARY_EXERCISE_DROPDOWN_MENU_BUTTON_CLICKED,
              );
              getToggleButtonProps().onClick?.(event);
            }}
            iconName="ellipsis-vertical"
            variant="secondaryNeutral"
            aria-label={t('misc.openMenu')}
          />
        );
      }}
      renderOption={(option) => (
        <>
          <div className="w-[296px]">
            <DropdownItem
              label={option.label}
              className={
                option.key === 'delete' ? 'text-content-alert-default' : ''
              }
            />
          </div>
          {option.hasSeparator && <div className="separator my-4" />}
        </>
      )}
      onSelect={(option) => option.action()}
    />
  );
};
